import { FormikProvider, useFormik, Form, FormikHelpers } from 'formik';
import * as Yup from "yup";
import { Icon } from "@iconify/react";
import React, { useState } from 'react';
import { TextField, IconButton, InputAdornment, Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";

export interface ISignupValues {
  email: string,
  fullName: string,
  phone?: string,
  password: string,
  passwordRepeat: string
}

interface ISignupFormProps {
  handleSubmitProp: (values: ISignupValues, actions: FormikHelpers<ISignupValues>) => void
  typedValues?: ISignupValues
}

const grid2 = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 } as const;

const SignupForm: React.FC<ISignupFormProps> = ({ typedValues, handleSubmitProp }) => {

  const location = window.location
  const baseURL = location.origin
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)

  const SignupSchema = Yup.object().shape({
    fullName: Yup.string().required('Nome completo obrigatório').min(3, 'Mínimo de 3 letras'),
    email: Yup.string().email('Endereço de e-mail inválido').required('E-mail obrigatorio'),
    phone: Yup.string(),
    password: Yup.string()
      .required('Senha obrigatória')
      .min(6, 'A senha deve ter no mínimo 6 dígitos')
      .matches(/([a-z])+/g, 'Digite ao menos uma letra minúscula')
      .matches(/([A-Z])+/g, 'Digite ao menos uma letra maiúscula')
      .matches(/([0-9])+/g, 'Digite ao menos um dígito numérico')
      .matches(/([!@#$%¨^&*()\-[\]{}+=/;:'"._])+/g, 'Digite ao menos um símbolo ex. @#!'),
    passwordRepeat: Yup.string()
      .required('Senha obrigatória')
      .oneOf([Yup.ref('password'), null], 'As senhas devem ser iguais')
  })

  const formik = useFormik<ISignupValues>({
    initialValues: {
      email: typedValues?.email || '',
      fullName: typedValues?.fullName || '',
      phone: typedValues?.phone || '',
      password: typedValues?.password || '',
      passwordRepeat: typedValues?.passwordRepeat || ''
    },
    validationSchema: SignupSchema,
    onSubmit: (values: ISignupValues, actions: FormikHelpers<ISignupValues>) => {
      setLoading(true)
      handleSubmitProp(values, actions)
    }
  })

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Nome completo"
          placeholder="Seu nome"
          {...getFieldProps('fullName')}
          inputProps={{ 'data-testid': 'fullName' }}
          error={Boolean(touched.fullName && errors.fullName)}
          helperText={touched.fullName && errors.fullName}
          sx={{ mb: 2 }}
        />

        <Box sx={{ ...grid2, mb: 2 }}>
          <TextField
            fullWidth
            autoComplete="email"
            type="email"
            label="Email"
            placeholder="seu@email.com"
            {...getFieldProps('email')}
            inputProps={{ 'data-testid': 'email' }}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />
          <TextField
            fullWidth
            label="Telefone"
            placeholder="(11) 99999-9999"
            {...getFieldProps('phone')}
            inputProps={{ 'data-testid': 'phone' }}
            error={Boolean(touched.phone && errors.phone)}
            helperText={touched.phone && errors.phone}
          />
        </Box>

        <Box sx={grid2}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Senha"
            {...getFieldProps('password')}
            inputProps={{ 'data-testid': 'password' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? "Ocultar senha" : "Mostrar senha"} placement="top">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
          <TextField
            fullWidth
            type={showPasswordRepeat ? "text" : "password"}
            label="Confirmar"
            {...getFieldProps('passwordRepeat')}
            inputProps={{ 'data-testid': 'passwordRepeat' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPasswordRepeat ? "Ocultar senha" : "Mostrar senha"} placement="top">
                    <IconButton onClick={() => setShowPasswordRepeat((s) => !s)} edge="end">
                      <Icon icon={showPasswordRepeat ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.passwordRepeat && errors.passwordRepeat)}
            helperText={touched.passwordRepeat && errors.passwordRepeat}
          />
        </Box>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          color="primary"
          loading={isSubmitting && loading}
          loadingIndicator={<CircularProgress color="inherit" size={18} />}
          sx={{ mt: 3, height: 54, fontSize: '15px', fontWeight: 600 }}
        >
          Criar conta
        </LoadingButton>

        <Typography sx={{ fontSize: '12px', color: 'text.secondary', textAlign: 'center', mt: 2, lineHeight: 1.5 }}>
          Ao criar a conta você concorda com os{' '}
          <Box component="a" href={baseURL + '/use-terms'} rel="noreferrer" target="_blank" sx={{ color: 'text.secondary', textDecoration: 'underline' }}>
            Termos de uso
          </Box>{' '}e a{' '}
          <Box component="a" href={baseURL + '/privacy-policy'} rel="noreferrer" target="_blank" sx={{ color: 'text.secondary', textDecoration: 'underline' }}>
            Política de privacidade
          </Box>.
        </Typography>
      </Form>
    </FormikProvider>
  )
}

export default SignupForm
